import { useTranslation } from 'react-i18next';
import { columnsWidth } from 'components/molecules/rpm-overview-table/constants';
import { useState, type FC } from 'react';
import ChevronBottom from 'components/atoms/icons/chevron-bottom';
import classNames from 'classnames';
import { useAssignPatient } from 'infrastructure/hooks/rpm/assign-tool/use-set-assign-patient';
import useUser from 'utils/useUser';
import BaseDropdownButton from 'components/atoms/baseButton/dropdown';
import { useCountdownTimer } from 'infrastructure/hooks/use-count-down-timer';

import s from './styles.module.scss';

import type { TDurationInMinutes } from 'infrastructure/hooks/rpm/assign-tool/use-set-assign-patient';
import type { IAssignee } from 'infrastructure/interfaces';

interface AssignCellProps {
  patientGuid: string;
  assignee?: IAssignee;
}
const AssignCell: FC<AssignCellProps> = ({ patientGuid, assignee }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { loginGuid } = useUser();
  const { isMobileDevice } = useUser();
  const { assignPatient, isLoading } = useAssignPatient();

  const isAssignedToOther = assignee && assignee.loginGuid !== loginGuid;
  const isAssignedToMe = assignee && assignee.loginGuid === loginGuid;
  const endTime = assignee?.expireAtTimestamp
    ? new Date(assignee.expireAtTimestamp)
    : undefined;

  useCountdownTimer({
    endTime,
    onFinish: () => {
      assignPatient({ patientGuid, action: 'unassign', silentUpdate: true });
    },
  });

  const label =
    assignee && isAssignedToMe ? t('labels.unassign') : t('labels.assign');

  const assignedToMeTitle = isAssignedToMe
    ? t('labels.assignedToMe')
    : undefined;
  const isAssignedToOtherTitle = isAssignedToOther
    ? assignee?.fullName
    : undefined;

  const assignedTitle = assignedToMeTitle ?? isAssignedToOtherTitle;

  const onItemClick = async (durationInMinutes: TDurationInMinutes) => {
    assignPatient({ patientGuid, durationInMinutes, action: 'assign' });
  };

  const handleUnassign = async () => {
    if (!assignee || isAssignedToOther) return;

    assignPatient({ patientGuid, action: 'unassign' });
  };

  const items = [
    {
      label: 'Assign Time Frame',
      key: '0',
      dataItem: 'label',
    },
    {
      label: '1 Hour',
      key: '1',
      onClick: () => onItemClick(60),
    },
    {
      label: '2 Hours',
      'data-cy': 'dropdown-edit-item',
      key: '2',
      onClick: () => onItemClick(120),
    },
    {
      label: '3 Hours',
      'data-cy': 'dropdown-edit-item',
      key: '3',
      onClick: () => onItemClick(180),
    },
  ];

  return (
    <div
      className={s.wrapper}
      style={{ width: isMobileDevice ? 100 : columnsWidth.assign }}
    >
      <BaseDropdownButton
        items={[]}
        dropdownProps={{
          menu: { items },
          open,
          onOpenChange: setOpen,
          placement: 'bottomRight',
          disabled: !!assignee,
          rootClassName: s.menu,
          trigger: ['click'],
        }}
        label={label}
        type="secondary"
        disabled={isLoading || !!isAssignedToOther}
        rightIcon={isAssignedToMe || isLoading ? undefined : <ChevronBottom />}
        loading={isLoading}
        onClick={handleUnassign}
        className={classNames(s['assign-button'], { [s.open]: open })}
        title={assignedTitle}
      />
    </div>
  );
};
export default AssignCell;
